import Quill from 'quill';

let registered = false;

/**
 * Switches align, size and font from CSS-class attributors (ql-align-*,
 * ql-size-*, ql-font-*) to inline-style attributors, so saved HTML
 * carries style="text-align/font-size/font-family" and renders correctly
 * anywhere with no stylesheet dependency — including consumers with no
 * Quill CSS, such as the student portal.
 *
 * Direction is different: `style="direction: rtl"` alone flows text
 * correctly, but the list-marker CSS (both Quill's own editor styles and
 * the consumer-side content styles) positions bullets/numbers via the
 * `ql-direction-rtl` class. A style-only attributor would leave new RTL
 * lists with markers on the wrong side everywhere. Direction therefore
 * registers as a dual attributor that writes BOTH the inline style (for
 * text flow without CSS) and the class (for the marker/indent rules).
 *
 * Must run before any `new Quill(...)`.
 */
export function registerInlineStyleFormats() {
	if (registered) return;

	const AlignStyle = Quill.import('attributors/style/align') as any;
	Quill.register(AlignStyle, true);

	// Size and font ship as CLASS attributors by default (ql-size-*,
	// ql-font-*), which render as nothing wherever Quill's stylesheet is
	// absent — and the student portal ships none. Registering the style
	// variants makes saved HTML carry font-size/font-family inline, so it
	// renders for students with no CSS dependency at all.
	const SizeStyle = Quill.import('attributors/style/size') as any;
	Quill.register(SizeStyle, true);

	const FontStyle = Quill.import('attributors/style/font') as any;
	Quill.register(FontStyle, true);

	const DirectionStyle = Quill.import('attributors/style/direction') as any;
	const StyleAttributor = DirectionStyle.constructor as new (
		attrName: string,
		keyName: string,
		options: unknown
	) => any;

	class DirectionStyleAndClass extends StyleAttributor {
		add(node: HTMLElement, value: unknown): boolean {
			const applied = super.add(node, value);
			if (applied && value === 'rtl') node.classList.add('ql-direction-rtl');
			return applied;
		}

		remove(node: HTMLElement): void {
			super.remove(node);
			node.classList.remove('ql-direction-rtl');
			if (!node.getAttribute('class')) node.removeAttribute('class');
		}

		value(node: HTMLElement): string {
			return (
				super.value(node) ||
				(node.classList.contains('ql-direction-rtl') ? 'rtl' : '')
			);
		}
	}

	const direction = new DirectionStyleAndClass('direction', 'direction', {
		scope: DirectionStyle.scope,
		whitelist: DirectionStyle.whitelist,
	});
	Quill.register(direction, true);

	registerVideoEmbedFormat();

	registered = true;
}

/**
 * Converts the share URLs people actually copy (YouTube watch/shorts,
 * youtu.be, Vimeo page, Drive file view) to their embeddable form.
 * A page URL inside an iframe renders a blank box — YouTube and friends
 * refuse framing — so pasting the obvious link would silently fail.
 *
 * Only hosts on the student backend's ContentSanitizer iframe allowlist
 * are worth emitting here; anything else is stripped when served to
 * students. Unrecognised URLs pass through untouched (already-embeddable
 * links like player.vimeo.com or iframe.mediadelivery.net included).
 */
export function normalizeVideoEmbedUrl(raw: string): string {
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		return raw;
	}
	const host = url.hostname.replace(/^(www|m)\./, '');
	if (host === 'youtu.be') {
		const id = url.pathname.slice(1).split('/')[0];
		if (id) return `https://www.youtube.com/embed/${id}`;
	}
	if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
		if (url.pathname.startsWith('/embed/')) return raw;
		const pathId = url.pathname.match(/^\/(?:shorts|live)\/([\w-]+)/);
		if (pathId) return `https://www.youtube.com/embed/${pathId[1]}`;
		const id = url.searchParams.get('v');
		if (id) return `https://www.youtube.com/embed/${id}`;
	}
	if (host === 'vimeo.com') {
		const id = url.pathname.match(/^\/(\d+)/);
		if (id) return `https://player.vimeo.com/video/${id[1]}`;
	}
	if (host === 'drive.google.com') {
		const id = url.pathname.match(/^\/file\/d\/([^/]+)/);
		if (id) return `https://drive.google.com/file/d/${id[1]}/preview`;
	}
	return raw;
}

/**
 * Replaces Quill's built-in video blot with one that runs the URL through
 * normalizeVideoEmbedUrl first — covering the toolbar prompt and pasted
 * iframes alike. Must run before any `new Quill(...)`.
 */
function registerVideoEmbedFormat() {
	const Video = Quill.import('formats/video') as any;

	class EmbedVideo extends Video {
		static sanitize(url: string): string {
			return Video.sanitize(normalizeVideoEmbedUrl(url));
		}
	}

	Quill.register(EmbedVideo, true);
}
